import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  CircularProgress,
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Divider,
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  VerifiedUser as VerifyIcon,
  InfoOutlined as InfoOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { loadStripe } from '@stripe/stripe-js';
import client from '../../API';

const stripePublishableKey =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_STRIPE_LIVE_PUBLISHABLE_KEY;

// ─────────────────────────────────────────────────────────────────
// Pre-flow informational dialog shown before Stripe FC opens
// ─────────────────────────────────────────────────────────────────
const DISCLAIMER_POINTS = [
  {
    headline: 'This is not Stripe as a payment method',
    body: 'You are not signing up for or paying through Stripe. This is purely a verification step.',
  },
  {
    headline: 'We partnered with Stripe to verify your bank',
    body: "Stripe's bank-linking technology lets us confirm your account is real and belongs to you — keeping your payouts safe.",
  },
  {
    headline: 'No Stripe account required',
    body: 'You only need the bank information you already have (account/routing numbers, or online banking login). Nothing more.',
  },
];

const BankLinkingInfoDialog = ({ open, onClose, onContinue }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xs"
    fullWidth
    PaperProps={{
      elevation: 4,
      sx: { borderRadius: 3, overflow: 'hidden' },
    }}
  >
    {/* Tinted header strip */}
    <Box
      sx={{
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        px: 3,
        py: 2.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 28 }} />
      <Box>
        <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
          Before we move forward
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.85 }}>
          Just a few things to know
        </Typography>
      </Box>
    </Box>

    <DialogContent sx={{ pt: 3, pb: 1 }}>
      <Stack spacing={2.5}>
        {DISCLAIMER_POINTS.map((point, idx) => (
          <Stack key={idx} direction="row" spacing={1.5} alignItems="flex-start">
            <CheckCircleOutlineIcon
              color="primary"
              sx={{ mt: 0.25, flexShrink: 0, fontSize: 22 }}
            />
            <Box>
              <Typography variant="body2" fontWeight={700} gutterBottom>
                {point.headline}
              </Typography>
              <Typography variant="body2" color="text.secondary" lineHeight={1.55}>
                {point.body}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </DialogContent>

    <Divider sx={{ mt: 2 }} />

    <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
      <Button
        onClick={onClose}
        color="inherit"
        size="medium"
        sx={{ borderRadius: 2, textTransform: 'none' }}
      >
        Go Back
      </Button>
      <Button
        onClick={onContinue}
        variant="contained"
        size="medium"
        startIcon={<BankIcon />}
        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flex: 1 }}
      >
        Got It — Link My Bank
      </Button>
    </DialogActions>
  </Dialog>
);

const StripeFCButton = ({
  onSuccess,
  onError,
  variant = 'contained',
  size = 'medium',
  label = 'Link Bank Account',
  fullWidth = false,
  disabled = false,
  sx = {},
  ...props
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [stripeInstance, setStripeInstance] = useState(null);
  const [connectAccountId, setConnectAccountId] = useState(null);
  const [connectedStripeInstance, setConnectedStripeInstance] = useState(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  // Controls the pre-flow info dialog
  const [showInfoDialog, setShowInfoDialog] = useState(false);

  const isFetchingSession = useRef(false);
  const hasReconciled = useRef(false);

  // Load Stripe.js on mount
  useEffect(() => {
    if (!stripePublishableKey) {
      setError('Stripe publishable key not configured');
      return;
    }
    loadStripe(stripePublishableKey).then(setStripeInstance);
  }, []);

  // Pull in any banks linked during Stripe-hosted onboarding (idempotent / best-effort)
  useEffect(() => {
    if (hasReconciled.current) return;
    hasReconciled.current = true;
    (async () => {
      try {
        const result = await client.bank.reconcileExternalAccounts();
        const wallet = result?.wallets?.[0];
        if (wallet) {
          onSuccess(wallet);
        }
      } catch {
        // Swallow — the FC modal still works as a fallback.
      }
    })();
  }, []);

  // Handle Express onboarding flow
  const handleExpressOnboarding = async () => {
    setLoading(true);
    setError(null);
    try {
      await client.bank.createExpressAccount();

      const refreshUrl = window.location.href;
      const returnUrl = (() => {
        const u = new URL(window.location.href);
        u.searchParams.set('stripe_onboarding', 'complete');
        return u.toString();
      })();
      const linkResponse = await client.bank.getExpressOnboardingLink({
        return_url: returnUrl,
        refresh_url: refreshUrl,
      });

      if (linkResponse.onboarding_url) {
        window.location.href = linkResponse.onboarding_url;
      } else {
        throw new Error('No onboarding URL received');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to start Stripe verification';
      setError(errorMessage);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch a fresh FC session
  const fetchSession = async () => {
    if (isFetchingSession.current) return;
    isFetchingSession.current = true;

    try {
      const response = await client.bank.createFCSession();
      if (response.client_secret) {
        setClientSecret(response.client_secret);
        setNeedsOnboarding(false);
        if (response.connect_account_id) {
          setConnectAccountId(response.connect_account_id);
          loadStripe(stripePublishableKey, { stripeAccount: response.connect_account_id })
            .then(setConnectedStripeInstance);
        }
      } else {
        throw new Error('No client_secret received from server');
      }
    } catch (err) {
      const errorCode = err.response?.data?.error_code;
      if (errorCode === 'express_onboarding_required') {
        setNeedsOnboarding(true);
      } else {
        const errorMessage = err.response?.data?.error || err.message || 'Failed to initialize bank linking';
        setError(errorMessage);
        if (onError) onError(err);
      }
    } finally {
      isFetchingSession.current = false;
    }
  };

  // Fetch initial session on mount
  useEffect(() => {
    fetchSession();
  }, []);

  const ready = needsOnboarding || !!(stripeInstance && clientSecret && connectedStripeInstance);

  // The actual Stripe flow — called after the user confirms the info dialog
  const proceedWithLinking = async () => {
    setShowInfoDialog(false);

    if (needsOnboarding) {
      await handleExpressOnboarding();
      return;
    }

    if (!stripeInstance) return;

    let secret = clientSecret;
    if (!secret) {
      await fetchSession();
      return;
    }

    setIsProcessing(true);
    setLoading(true);
    setError(null);

    try {
      const result = await connectedStripeInstance.collectBankAccountToken({
        clientSecret: secret,
      });

      if (result.error) {
        throw new Error(result.error.message || 'Bank linking was cancelled');
      }

      const accounts = result.financialConnectionsSession?.accounts || [];
      if (accounts.length === 0) {
        return;
      }

      const fcAccountId = accounts[0].id;
      const walletData = await client.bank.linkFCAccount({
        fc_account_id: fcAccountId,
        ...(result.token?.id && { bank_token: result.token.id }),
      });

      onSuccess(walletData);
    } catch (err) {
      if (
        err.message?.includes('cancelled') ||
        err.message?.includes('canceled') ||
        err.message?.includes('No bank account')
      ) {
        return;
      }

      const errorMessage = err.response?.data?.error || err.message || 'Failed to link bank account';
      setError(errorMessage);
      if (onError) onError(err);
    } finally {
      setLoading(false);
      setIsProcessing(false);
      setClientSecret(null);
      setConnectedStripeInstance(null);
      fetchSession();
    }
  };

  // Button click — open the info dialog first (unless already processing)
  const handleClick = () => {
    if (isProcessing || loading || !ready) return;
    setShowInfoDialog(true);
  };

  const buttonLabel = loading
    ? 'Connecting...'
    : needsOnboarding
      ? 'Complete Verification'
      : !ready
        ? 'Loading...'
        : label;

  const buttonIcon = loading
    ? <CircularProgress size={20} />
    : needsOnboarding
      ? <VerifyIcon />
      : <BankIcon />;

  return (
    <Box sx={sx}>
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      <Button
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled || loading || !ready}
        onClick={handleClick}
        startIcon={buttonIcon}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
        }}
        {...props}
      >
        {buttonLabel}
      </Button>

      <BankLinkingInfoDialog
        open={showInfoDialog}
        onClose={() => setShowInfoDialog(false)}
        onContinue={proceedWithLinking}
      />
    </Box>
  );
};

StripeFCButton.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func,
  variant: PropTypes.string,
  size: PropTypes.string,
  label: PropTypes.string,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
};

export default StripeFCButton;